import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { KizenTypography } from '__app/typography';
import { Button } from '@kizen/kds/Button';
import { PillDropdown } from 'components/Inputs/PillDropdown';
import { PILL_TYPES } from 'components/Inputs/PillDropdown/constants';
import type {
  InstalledAppType,
  InstalledAttention,
  InstalledFilters,
} from './rows';

const StyledLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StyledHeading = styled(KizenTypography)`
  font-size: 12px;
  text-transform: uppercase;
`;

const noop = () => {};

export const QuickFilters: FC<{
  filters: InstalledFilters;
  onChange: (filters: InstalledFilters) => void;
  showBusinessType: boolean;
}> = ({ filters, onChange, showBusinessType }) => {
  const { t } = useTranslation();

  const typeOptions = useMemo(
    () => [
      ...(showBusinessType
        ? [{ value: 'business', label: t('Business') }]
        : []),
      { value: 'marketplace', label: t('Marketplace') },
    ],
    [t, showBusinessType]
  );

  const attentionOptions = useMemo(
    () => [
      { value: 'errors', label: t('Errors') },
      { value: 'warnings', label: t('Warnings') },
    ],
    [t]
  );

  return (
    <StyledLayout>
      <StyledHeading color="var(--color-font-placeholder)" weight="bold">
        {t('Quick Filters')}
      </StyledHeading>
      <span data-qa-id="installed-quick-filter-type">
        <PillDropdown
          pillType={PILL_TYPES.singleselect.type}
          value={filters.type}
          title={t('Type')}
          options={typeOptions}
          onChange={(option: { value: InstalledAppType } | null) =>
            onChange({ ...filters, type: option?.value ?? null })
          }
          onOpen={noop}
          onClose={noop}
          autoFocus={false}
        />
      </span>
      <span data-qa-id="installed-quick-filter-attention">
        <PillDropdown
          pillType={PILL_TYPES.singleselect.type}
          value={filters.attention}
          title={t('Needs attention')}
          options={attentionOptions}
          onChange={(option: { value: InstalledAttention } | null) =>
            onChange({ ...filters, attention: option?.value ?? null })
          }
          onOpen={noop}
          onClose={noop}
          autoFocus={false}
        />
      </span>
      {filters.type || filters.attention ? (
        <Button
          variant="text"
          color="primary"
          onClick={() => onChange({ type: null, attention: null })}
          qa={{ id: 'installed-quick-filters-clear' }}
        >
          {t('Clear')}
        </Button>
      ) : null}
    </StyledLayout>
  );
};
